import { useModal } from "@ebay/nice-modal-react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import Banner from "ds/components/Banner";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Input from "ds/components/Input";
import useCreateStackFromIntent from "shared/IntentProject/useCreateStackFromIntent";
import useStackNameValidation from "shared/Stack/useStackNameValidation";
import { getDrawerFormFix } from "utils/css";

type CreateStackFormFields = {
  stackName: string;
  importState: boolean;
};

type IntentProjectCreateStackDrawerProps = {
  projectId: string;
  projectName: string;
};

const IntentProjectCreateStackDrawer = createDrawer(
  ({ projectId, projectName }: IntentProjectCreateStackDrawerProps) => {
    const drawer = useModal();
    const navigate = useNavigate();

    const [createStackFromIntent, { loading }] = useCreateStackFromIntent();

    const validateStackName = useStackNameValidation();

    const {
      handleSubmit,
      formState: { isDirty, isValid },
      control,
    } = useForm<CreateStackFormFields>({
      defaultValues: {
        stackName: projectName,
        importState: true,
      },
      mode: "onChange",
    });

    const onSubmit = (formData: CreateStackFormFields) => {
      createStackFromIntent(
        {
          project: projectId,
          stackName: formData.stackName.trim(),
          importState: formData.importState,
        },
        (data) => {
          if (data?.stackCreateFromIntent) {
            drawer.hide();
            navigate(`/stack/${data.stackCreateFromIntent.id}`);
          }
        }
      );
    };

    return (
      <DrawerForm isDirty={isDirty}>
        <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title="Create a stack" />
            <DrawerCloseIcon />
          </DrawerHeader>

          <DrawerBody gap="x-large">
            <Banner variant="warning">
              This will create a new stack from the intent project, transferring its context,
              environment variables, and integrations.
            </Banner>

            <Controller
              name="stackName"
              control={control}
              rules={{ validate: validateStackName }}
              render={({ field, fieldState }) => (
                <FormField label="Stack name" error={fieldState.error?.message}>
                  {({ ariaInputProps }) => (
                    <Input
                      placeholder="Enter stack name"
                      ref={field.ref}
                      error={!!fieldState.error}
                      value={field.value}
                      onChange={field.onChange}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="importState"
              control={control}
              render={({ field }) => (
                <FormToggleField
                  variant="checkbox"
                  title="Transfer state"
                  description="Import the current state from the intent project into the new stack."
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </DrawerBody>

          <DrawerFooter>
            <DrawerFooterActions>
              <DrawerCancelButton />
              <Button variant="primary" type="submit" loading={loading} disabled={!isValid}>
                Create
              </Button>
            </DrawerFooterActions>
          </DrawerFooter>
        </form>
      </DrawerForm>
    );
  }
);

export const showIntentProjectCreateStackDrawer = createDrawerTrigger(
  IntentProjectCreateStackDrawer
);
